import { UserRole, OrganizationRole } from '@/types/enums';
import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Menu,
  MenuItem,
  Typography,
  Avatar,
  ListItemIcon,
  ListItemText,
  List,
  ListItem,
  ListItemButton,
  Collapse,
} from '@mui/material';
import {
  KeyboardArrowDown,
  Business,
  Check,
} from '@mui/icons-material';
import { PreloadedAvatar } from './common/PreloadedAvatar';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '@/hooks/redux';
import { useQueryClient } from 'react-query';
import { organizationService } from '@/services/organizationService';
import { playerService } from '@/services/playerService';
import { leagueService } from '@/services/leagueService';
import { loginSuccess } from '@/store/authSlice';
import { authManager } from '@/services/authManager';

interface OrganizationSwitcherProps {
  onClose?: () => void;
  isMobileDrawer?: boolean;
  compact?: boolean;
}

const OrganizationSwitcher: React.FC<OrganizationSwitcherProps> = ({ onClose, isMobileDrawer = false, compact = false }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [photoRefreshKey, setPhotoRefreshKey] = useState(Date.now());
  // undefined = loading, null = no photo, string = has photo
  const [orgPhotos, setOrgPhotos] = useState<Record<string, string | null>>({});
  const [mobileExpanded, setMobileExpanded] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const { user } = useAppSelector((state) => state.auth);

  // Use organizations from Redux instead of local state
  const organizations = user?.availableOrganizations || [];

  // Prefetch data for an organization
  const prefetchOrgData = (orgId: string) => {
    queryClient.prefetchQuery(['players', orgId], playerService.getPlayers);
    queryClient.prefetchQuery(['leagues', orgId], async () => {
      const response = await leagueService.getLeagues(1, 100);
      return response.data || [];
    });
  };

  const currentOrg = user?.currentOrganizationId 
    ? organizations.find((org: any) => org.organizationId === user.currentOrganizationId)
    : null;

  // Get current org photo: undefined = loading, null = no photo, string = has photo
  const currentOrgPhoto = currentOrg
    ? (currentOrg.organizationId in orgPhotos ? orgPhotos[currentOrg.organizationId] : undefined)
    : null;

  // Listen for photo updates
  useEffect(() => {
    const handlePhotoUpdate = () => setPhotoRefreshKey(Date.now());
    window.addEventListener('organizationPhotoUpdated', handlePhotoUpdate);
    return () => window.removeEventListener('organizationPhotoUpdated', handlePhotoUpdate);
  }, []);

  // Reset mobile expanded state when drawer becomes compact
  useEffect(() => {
    if (compact) {
      setMobileExpanded(false);
    }
  }, [compact]);

  // Preload all org photos
  useEffect(() => {
    const loadPhotos = async () => {
      const token = await authManager.getAccessToken();
      if (!token) return;

      const photos: Record<string, string | null> = {};
      for (const org of organizations) {
        try {
          const res = await fetch(`/api/v1/organizations/${org.organizationId}/photo/small?t=${photoRefreshKey}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const data = await res.json();
          // Store URL if found, null if no photo
          photos[org.organizationId] = data.url || null;
        } catch (err) {
          photos[org.organizationId] = null; // Error = no photo
        }
      }
      setOrgPhotos(photos);
    };

    if (organizations.length > 0) {
      loadPhotos();
    }
  }, [organizations, photoRefreshKey]);

  // Load organizations and restore last selected organization
  useEffect(() => {
    if (organizations.length > 0 && user) {
      const lastSelectedOrgId = localStorage.getItem('lastSelectedOrganizationId');
      
      // If user doesn't have a current organization but has a saved preference
      if (!user.currentOrganizationId && lastSelectedOrgId) {
        const savedOrg = organizations.find((org: any) => org.organizationId === lastSelectedOrgId);
        if (savedOrg) {
          handleSwitchOrganization(lastSelectedOrgId, false); // Don't preserve context on initial load
        }
      }
    }
  }, [organizations, user]);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    const isOwner = currentOrg?.role === OrganizationRole.OWNER;
    const isSuperAdmin = user?.role === UserRole.SUPER_ADMIN;
    
    if (organizations.length > 1 || isOwner || isSuperAdmin) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSwitchOrganization = async (organizationId: string, preserveContext: boolean = true) => {
    if (organizationId === user?.currentOrganizationId) {
      handleClose();
      onClose?.();
      return;
    }

    const targetOrg = organizations.find((org: any) => org.organizationId === organizationId);

    handleClose(); // Close menu immediately for smoother UX
    
    // Prefetch data for the new organization before switching
    prefetchOrgData(organizationId);
    
    try {
      const response = await organizationService.switchOrganization(organizationId);
      
      // Use the updated user object from the server response
      const updatedUser = response.user ? {
        ...response.user,
        role: response.user.role as UserRole,
        availableOrganizations: response.user.availableOrganizations?.map(org => ({
          ...org,
          role: org.role as OrganizationRole
        }))
      } : {
        ...user!,
        currentOrganizationId: organizationId,
        availableOrganizations: user!.availableOrganizations?.map((org: any) => 
          org.organizationId === organizationId 
            ? { ...org, organizationName: response.currentOrganization.name, role: org.role as OrganizationRole }
            : { ...org, role: org.role as OrganizationRole }
        ),
      };
      
      // Update the auth manager so the API interceptor uses the new org's JWT
      authManager.updateAccessToken(response.token);

      dispatch(loginSuccess({
        token: response.token,
        user: updatedUser,
      }));

      // Store the new token and remember organization selection
      localStorage.setItem('token', response.token);
      localStorage.setItem('lastSelectedOrganizationId', organizationId);
      
      // Navigate to appropriate page based on current location and new role
      if (preserveContext && targetOrg) {
        const currentPath = location.pathname;
        let targetPath = currentPath; // Default: preserve current page
        const isSuperAdmin = user?.role === UserRole.SUPER_ADMIN;

        // Super admins can stay on any page
        if (!isSuperAdmin) {
          // If on admin console, check if user has access in new org
          if (currentPath === '/settings') {
            const isOwnerOrAdmin = targetOrg.role === OrganizationRole.OWNER || targetOrg.role === OrganizationRole.ADMIN;
            targetPath = isOwnerOrAdmin ? '/settings' : '/dashboard';
          }
          // If on system admin, only super admins can access
          else if (currentPath === '/admin') {
            targetPath = '/dashboard';
          }
        }

        navigate(targetPath);
      }

      // Close parent drawer if callback provided
      onClose?.();
    } catch (error) {
      console.error('Failed to switch organization:', error);
    }
  };

  const OrgAvatar: React.FC<{ photoUrl: string | null | undefined }> = ({ photoUrl }) => {
    return (
      <Box sx={{ position: 'relative', display: 'inline-block' }}>
        <PreloadedAvatar
          src={photoUrl}
          size={40}
          fallbackIcon={<Business sx={{ fontSize: 24 }} />}
          sx={{
            border: '2px solid',
            borderColor: 'divider'
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -2,
            right: -2,
            width: 16,
            height: 16,
            borderRadius: '50%',
            bgcolor: 'background.paper',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Business sx={{ fontSize: 10, color: 'text.secondary' }} />
        </Box>
      </Box>
    );
  };

  if (!currentOrg) {
    return (
      <Box display="flex" alignItems="center" gap={1}>
        <Avatar sx={{
          width: 40,
          height: 40,
          bgcolor: 'grey.300',
          border: '2px solid',
          borderColor: 'divider'
        }}>
        </Avatar>
        <Typography variant="body2" color="text.secondary">
          No Organization
        </Typography>
      </Box>
    );
  }

  // Mobile drawer expandable version
  if (isMobileDrawer) {
    const isOwner = currentOrg?.role === OrganizationRole.OWNER;
    const isSuperAdmin = user?.role === UserRole.SUPER_ADMIN;
    const showExpand = organizations.length > 1 || isOwner || isSuperAdmin;
    const canExpand = organizations.length > 1;

    return (
      <Box>
        <ListItem disablePadding>
          <ListItemButton 
            onClick={showExpand && canExpand ? () => setMobileExpanded(!mobileExpanded) : undefined}
            disabled={!showExpand || !canExpand}
            sx={{
              '&.Mui-disabled': {
                opacity: 1,
              },
              cursor: 'default',
              pointerEvents: (showExpand && canExpand) ? 'auto' : 'none',
            }}
          >
            <ListItemIcon sx={{ minWidth: 48 }}>
              <OrgAvatar photoUrl={currentOrgPhoto} />
            </ListItemIcon>
            <ListItemText 
              primary={currentOrg.organizationName}
              primaryTypographyProps={{ variant: 'body2', fontWeight: 'medium' }}
            />
            {canExpand && showExpand && (
              <KeyboardArrowDown 
                sx={{ 
                  transform: mobileExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease-in-out'
                }} 
              />
            )}
          </ListItemButton>
        </ListItem>
        {showExpand && canExpand && !compact && (
          <Collapse in={mobileExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {organizations.map((org: any) => (
                <ListItem key={org.id} disablePadding>
                  <ListItemButton
                    sx={{ pl: 4 }}
                    onClick={() => handleSwitchOrganization(org.organizationId)}
                  >
                    <ListItemIcon sx={{ minWidth: 48 }}>
                      <PreloadedAvatar
                        src={orgPhotos[org.organizationId]}
                        size={32}
                        fallbackIcon={<Business sx={{ fontSize: 20 }} />}
                      />
                    </ListItemIcon>
                    <ListItemText 
                      primary={org.organizationName}
                      secondary={org.role}
                      primaryTypographyProps={{ variant: 'body2' }}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                    {org.organizationId === user?.currentOrganizationId && (
                      <Check fontSize="small" color="primary" />
                    )}
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Collapse>
        )}
      </Box>
    );
  }

  // Single organization - show name and subscription, make clickable for owners/super admins
  if (organizations.length === 1) {
    const isOwner = currentOrg?.role === OrganizationRole.OWNER;
    const isSuperAdmin = user?.role === UserRole.SUPER_ADMIN;
    const showDropdown = isOwner || isSuperAdmin;

    return (
      <Box
        onClick={showDropdown ? handleClick : undefined}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: 'default',
          padding: '6px 8px',
          borderRadius: 1,
          pointerEvents: showDropdown ? 'auto' : 'none',
        }}
      >
        <OrgAvatar photoUrl={currentOrgPhoto} />
        <Box sx={{ 
          display: 'none',
          '@media (min-width: 1081px)': { display: 'block', maxWidth: 180 },
          '@media (min-width: 1280px)': { maxWidth: 'none' },
          transition: 'all 0.2s ease-in-out'
        }}>
          <Typography variant="body2" fontWeight="medium" noWrap sx={{ transition: 'all 0.2s ease-in-out' }}>
            {currentOrg.organizationName}
          </Typography>
        </Box>
      </Box>
    );
  }

  // Multiple organizations - show dropdown
  return (
    <>
      <Button
        onClick={handleClick}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 1,
          textTransform: 'none',
          color: 'text.primary',
          '&:hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          },
          '& .MuiButton-endIcon': {
            marginLeft: '2px'
          }
        }}
        endIcon={<KeyboardArrowDown />}
      >
        <OrgAvatar photoUrl={currentOrgPhoto} />
        <Box textAlign="left" sx={{ 
          display: 'none',
          '@media (min-width: 1081px)': { display: 'block', maxWidth: 180 },
          '@media (min-width: 1280px)': { maxWidth: 'none' },
          transition: 'all 0.2s ease-in-out'
        }}>
          <Typography variant="body2" fontWeight="medium" noWrap sx={{ transition: 'all 0.2s ease-in-out' }}>
            {currentOrg.organizationName}
          </Typography>
        </Box>
      </Button>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          sx: {
            minWidth: 280,
            mt: -0.5,
          },
        }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
      >
        <Box sx={{ px: 2, py: 1 }}>
          <Typography variant="overline" color="text.secondary">
            Switch Organization
          </Typography>
        </Box>
        
        {organizations.map((org: any) => (
          <MenuItem
            key={org.id}
            onClick={() => handleSwitchOrganization(org.organizationId)}
            onMouseEnter={() => prefetchOrgData(org.organizationId)}
            sx={{ py: 1.5, pl: 2, pr: 2 }}
          >
            <ListItemIcon sx={{ minWidth: 56 }}>
              <PreloadedAvatar
                src={orgPhotos[org.organizationId]}
                size={40}
                fallbackIcon={<Business sx={{ fontSize: 24 }} />}
                sx={{ border: '2px solid', borderColor: 'divider' }}
              />
            </ListItemIcon>
            <ListItemText
              sx={{ ml: 2 }}
              primary={
                <Typography variant="body2" fontWeight="medium">
                  {org.organizationName}
                </Typography>
              }
              secondary={
                <Typography variant="caption" color="text.secondary">
                  {org.role}
                </Typography>
              }
            />
            {org.organizationId === user?.currentOrganizationId && (
              <ListItemIcon sx={{ minWidth: 'auto', ml: 1 }}>
                <Check fontSize="small" color="primary" />
              </ListItemIcon>
            )}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

export default OrganizationSwitcher;
